import { useState, useCallback } from 'react'
import { useToasts } from 'react-toast-notifications'
import { ApolloError } from '@apollo/client'
import { CallBookingParticipantFragment } from '../../../videoCallBookingHeaderQueries.generated'
import { useScheduleVideoCallByCareCoordinatorMutation } from './scheduleVideoCallByCareCoordinator.generated'
import { useGetParticipantVideoCallBookingInformation } from '../../../useGetParticipantVideoCallBookingInformation'

const FALLBACK_TIMEZONE = 'America/Los_Angeles'

interface UseBookCallParams {
  participant: CallBookingParticipantFragment
  coachEmail: string | null
  startTime: Date | null
  availabilityCalendarEventID?: string | null
}

const useBookCall = ({ participant, coachEmail, startTime, availabilityCalendarEventID = null }: UseBookCallParams) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [scheduleVideoCall] = useScheduleVideoCallByCareCoordinatorMutation()
  const { refetch: refetchBookingData } = useGetParticipantVideoCallBookingInformation(participant.uid)
  const { addToast } = useToasts()

  const bookCall = useCallback(async () => {
    if (!coachEmail || !startTime) return

    setLoading(true)
    setSuccess(false)

    try {
      const timezone = participant.privateUserData?.timezone ?? FALLBACK_TIMEZONE
      if (!participant.privateUserData?.timezone) {
        console.warn('Participant timezone is missing, using default', {
          participantUID: participant.uid,
          fallbackTimezone: FALLBACK_TIMEZONE,
        })
      }

      await scheduleVideoCall({
        variables: {
          uid: participant.uid,
          callInput: {
            availabilityCalendarEventID: availabilityCalendarEventID ?? null,
            startTime,
            timezone,
            coachEmail,
          },
          acceptedConsentIDs: null,
        },
      })

      await refetchBookingData()

      setLoading(false)
      setSuccess(true)
      addToast('Intake call has been successfully scheduled', { appearance: 'success' })

      setTimeout(() => {
        setSuccess(false)
      }, 750)
    } catch (error) {
      console.error('Failed to schedule call:', error)
      setLoading(false)

      if (error instanceof ApolloError && error?.graphQLErrors[0]?.extensions?.clientError) {
        addToast(error.message, { appearance: 'error' })
      } else {
        addToast('Failed to schedule intake call', { appearance: 'error' })
      }
    }
  }, [participant, coachEmail, startTime, availabilityCalendarEventID, scheduleVideoCall, refetchBookingData, addToast])

  return { loading, success, bookCall }
}

export default useBookCall
